/**
 * Edit Dashboard (mandate §5) — the Widgets screen's edit sheet. Each switch writes
 * `me.dashboard.update` and the grid re-renders from the response; a hidden widget leaves the grid, a shown
 * one returns, and a fresh account shows everything.
 */
import type { ViewModel } from '@/screens/generated/view-model';
import { css } from '@pointfive/ui';

const overlay = css(
  'position:fixed;inset:0;z-index:70;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--bg) 55%, transparent);backdrop-filter:blur(2px)',
);
const sheet = css(
  'width:380px;max-width:calc(100vw - 40px);border-radius:16px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-float);padding:18px',
);
const row = css('display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--l6)');
const ghostBtn = css(
  'font-size:11.5px;padding:7px 14px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer',
);
const knobBtn = (trackBg: string) =>
  css(
    `width:34px;height:20px;border-radius:99px;border:none;background:${trackBg};cursor:pointer;position:relative;flex:none;padding:0`,
  );

export function DashboardEditor({ m }: { m: Partial<ViewModel> }) {
  if (!m.dashEditOpen) return null;
  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Edit dashboard widgets">
      <div style={sheet}>
        <h2 style={css('font-size:13px;font-weight:600;margin:0 0 6px')}>Edit Dashboard</h2>
        <p style={css('font-size:11.5px;color:var(--text-mid);line-height:1.6;margin:0 0 8px')}>
          Choose which widgets the dashboard shows. Changes save as you flip them.
        </p>
        {(m.dashWidgets ?? []).map((w) => (
          <div key={String(w['key'])} style={row}>
            <span style={css('flex:1;font-size:12px')}>{String(w['label'])}</span>
            <button
              onClick={w['toggle'] as () => void}
              aria-label={(w['visible'] ? 'Hide ' : 'Show ') + String(w['label'])}
              aria-pressed={!!w['visible']}
              style={knobBtn(String(w['trackBg']))}
            >
              <span
                style={css(
                  `position:absolute;top:2px;left:${w['visible'] ? '16px' : '2px'};width:16px;height:16px;border-radius:99px;background:${String(w['knobBg'])};transition:left .15s`,
                )}
              ></span>
            </button>
          </div>
        ))}
        <div style={css('display:flex;justify-content:flex-end;margin-top:14px')}>
          <button style={ghostBtn} onClick={m.dashEditClose} aria-label="Close the widget editor">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
